import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PageHeader,
  Card,
  DataTable,
  Badge,
  Button,
  Toast,
  EmptyState,
  ConfirmModal,
} from '../ui';
import Drawer from './Drawer';
import {
  getCategoryTreeNodes,
  createCategoryNode,
  updateCategoryNode,
  deleteCategoryNode,
  listCategoryProducts,
  assignCategoryProducts,
} from '../../services/pimApi';
import './pim.css';

const PAGE_SIZE = 50;

function flattenNodes(nodes, depth = 0, out = []) {
  nodes.forEach((n) => {
    out.push({ ...n, depth });
    flattenNodes(n.children || [], depth + 1, out);
  });
  return out;
}

function TreeNode({ node, depth, selectedId, expanded, onToggle, onSelect }) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node.id);
  return (
    <>
      <div
        className={`pim-cat-node${selectedId === node.id ? ' active' : ''}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 8px',
          paddingLeft: 8 + depth * 18,
          cursor: 'pointer',
          borderRadius: 6,
          background: selectedId === node.id ? 'rgba(37, 99, 235, 0.10)' : undefined,
        }}
        onClick={() => onSelect(node)}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.id);
          }}
          style={{
            width: 18,
            border: 'none',
            background: 'none',
            cursor: hasChildren ? 'pointer' : 'default',
            color: '#667',
            padding: 0,
          }}
          aria-label={hasChildren ? (isOpen ? 'Collapse' : 'Expand') : undefined}
        >
          {hasChildren ? (isOpen ? '▾' : '▸') : '·'}
        </button>
        <span style={{ flex: 1, fontSize: 14, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {node.label}
          {node.is_holding_area && <Badge variant="warning">Holding</Badge>}
          {node.enabled === false && <Badge variant="neutral">Disabled</Badge>}
        </span>
        <span style={{ fontSize: 12, color: '#99a' }}>{node.product_count}</span>
      </div>
      {hasChildren && isOpen &&
        node.children.map((c) => (
          <TreeNode
            key={c.id}
            node={c}
            depth={depth + 1}
            selectedId={selectedId}
            expanded={expanded}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </>
  );
}

export default function CategoryTreeEditor() {
  const { code } = useParams();
  const [tree, setTree] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  // Right pane
  const [products, setProducts] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);

  // Dialogs
  const [nodeDrawer, setNodeDrawer] = useState(null); // {mode:'create'|'edit'|'move', node?, parent?}
  const [nodeForm, setNodeForm] = useState({ code: '', label: '', parent_id: null, sort_order: 0 });
  const [deleting, setDeleting] = useState(null);
  const [assignDrawer, setAssignDrawer] = useState(false);
  const [assignSkus, setAssignSkus] = useState('');

  const flat = useMemo(() => flattenNodes(nodes), [nodes]);

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCategoryTreeNodes(code);
      setTree(data.tree);
      setNodes(data.nodes || []);
      // Keep the selected node fresh after mutations.
      setSelected((prev) => {
        if (!prev) return prev;
        const again = flattenNodes(data.nodes || []).find((n) => n.id === prev.id);
        return again || null;
      });
    } catch (e) {
      setToast({ message: `Failed to load tree "${code}"`, error: true });
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const loadProducts = useCallback(async () => {
    if (!selected) {
      setProducts({ items: [], total: 0 });
      return;
    }
    setProductsLoading(true);
    try {
      const data = await listCategoryProducts(selected.id, {
        include_descendants: includeDescendants,
        limit: PAGE_SIZE,
        offset,
      });
      setProducts(data);
    } catch (e) {
      setToast({ message: 'Failed to load products for this category', error: true });
    } finally {
      setProductsLoading(false);
    }
  }, [selected, includeDescendants, offset]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectNode = (node) => {
    setSelected(node);
    setOffset(0);
  };

  const expandAll = () => setExpanded(new Set(flat.map((n) => n.id)));
  const collapseAll = () => setExpanded(new Set());

  // ── Node CRUD ──

  const openCreateNode = (parent) => {
    setNodeForm({ code: '', label: '', parent_id: parent ? parent.id : null, sort_order: 0 });
    setNodeDrawer({ mode: 'create', parent });
  };

  const openEditNode = (node) => {
    setNodeForm({
      code: node.code,
      label: node.label,
      parent_id: node.parent_id,
      sort_order: node.sort_order || 0,
    });
    setNodeDrawer({ mode: 'edit', node });
  };

  const saveNode = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (nodeDrawer.mode === 'create') {
        if (!nodeForm.code.trim() || !nodeForm.label.trim()) {
          setToast({ message: 'Code and label are required', error: true });
          setBusy(false);
          return;
        }
        await createCategoryNode({
          tree_code: code,
          code: nodeForm.code.trim(),
          parent_id: nodeForm.parent_id,
          labels: { en_AU: nodeForm.label.trim(), en_NZ: nodeForm.label.trim() },
          sort_order: Number(nodeForm.sort_order) || 0,
        });
        setToast({ message: `Category "${nodeForm.label}" created` });
      } else {
        const node = nodeDrawer.node;
        const payload = {
          labels: { ...node.labels, en_AU: nodeForm.label.trim(), en_NZ: nodeForm.label.trim() },
          sort_order: Number(nodeForm.sort_order) || 0,
        };
        // parent move: API uses 0 as "top level" sentinel.
        if ((nodeForm.parent_id ?? null) !== (node.parent_id ?? null)) {
          payload.parent_id = nodeForm.parent_id ?? 0;
        }
        await updateCategoryNode(node.id, payload);
        setToast({ message: `Category "${nodeForm.label}" updated` });
      }
      setNodeDrawer(null);
      loadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Save failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteNode = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    try {
      await deleteCategoryNode(deleting.id, true);
      setToast({ message: `Category "${deleting.label}" deleted` });
      if (selected?.id === deleting.id) setSelected(null);
      setDeleting(null);
      loadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({
        message: typeof detail === 'string' ? detail : detail?.message || 'Delete failed',
        error: true,
      });
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  // ── Assignments ──

  const removeProduct = async (sku) => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await assignCategoryProducts(selected.id, { remove: [sku] });
      setToast({ message: `${sku} removed from "${selected.label}"` });
      loadProducts();
      loadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Remove failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const submitAssign = async () => {
    if (!selected || busy) return;
    const skus = assignSkus
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!skus.length) {
      setToast({ message: 'Enter at least one SKU', error: true });
      return;
    }
    setBusy(true);
    try {
      const res = await assignCategoryProducts(selected.id, { add: skus });
      setToast({ message: `${res.added} SKU(s) assigned to "${selected.label}"` });
      setAssignDrawer(false);
      setAssignSkus('');
      loadProducts();
      loadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Assign failed', error: true });
    } finally {
      setBusy(false);
    }
  };

  const productColumns = [
    {
      key: 'sku',
      header: 'SKU',
      render: (r) => (
        <Link to={`/pim/products/${encodeURIComponent(r.sku)}`}>
          <code>{r.sku}</code>
        </Link>
      ),
    },
    { key: 'name', header: 'Name', render: (r) => r.product_name || r.name || '—' },
    { key: 'brand', header: 'Brand', render: (r) => r.brand || '—' },
    { key: 'lifecycle', header: 'Lifecycle', render: (r) => r.product_lifecycle || '—' },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Button size="sm" variant="ghost" onClick={() => removeProduct(r.sku)} disabled={busy}>
          Remove
        </Button>
      ),
    },
  ];

  const parentOptions = useMemo(() => {
    // For a move: exclude the node itself and its descendants.
    if (!nodeDrawer || nodeDrawer.mode === 'create') return flat;
    const excluded = new Set();
    const mark = (n) => {
      excluded.add(n.id);
      (n.children || []).forEach(mark);
    };
    const target = flat.find((n) => n.id === nodeDrawer.node?.id);
    if (target) {
      const findInTree = (list, id) => {
        for (const n of list) {
          if (n.id === id) return n;
          const found = findInTree(n.children || [], id);
          if (found) return found;
        }
        return null;
      };
      const full = findInTree(nodes, nodeDrawer.node.id);
      if (full) mark(full);
    }
    return flat.filter((n) => !excluded.has(n.id));
  }, [flat, nodes, nodeDrawer]);

  return (
    <>
      <PageHeader
        title={tree ? `Tree: ${tree.label}` : `Tree: ${code}`}
        sub="Manage categories and the products assigned to them."
        crumbs={[{ label: 'Category trees', to: '/pim/categories' }, { label: tree?.label || code }]}
        actions={<Button onClick={() => openCreateNode(null)}>New top-level category</Button>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) 1fr', gap: 16, alignItems: 'start' }}>
        <Card flush>
          <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Button size="sm" variant="ghost" onClick={expandAll}>Expand all</Button>
            <Button size="sm" variant="ghost" onClick={collapseAll}>Collapse all</Button>
          </div>
          <div style={{ padding: 8, maxHeight: '70vh', overflowY: 'auto' }}>
            {loading ? (
              <p className="pim-muted" style={{ padding: 8 }}>Loading…</p>
            ) : nodes.length === 0 ? (
              <EmptyState
                title="Empty tree"
                body="Add a top-level category to get started."
                action={<Button size="sm" onClick={() => openCreateNode(null)}>New category</Button>}
              />
            ) : (
              nodes.map((n) => (
                <TreeNode
                  key={n.id}
                  node={n}
                  depth={0}
                  selectedId={selected?.id}
                  expanded={expanded}
                  onToggle={toggle}
                  onSelect={selectNode}
                />
              ))
            )}
          </div>
        </Card>

        <Card flush>
          {selected ? (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: 14 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <strong style={{ fontSize: 15 }}>{selected.label}</strong>{' '}
                  <code style={{ fontSize: 12, color: '#667' }}>{selected.code}</code>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={includeDescendants}
                    onChange={(e) => { setOffset(0); setIncludeDescendants(e.target.checked); }}
                  />
                  Include subcategories
                </label>
                <Button size="sm" onClick={() => setAssignDrawer(true)}>Assign SKUs</Button>
                <Button size="sm" variant="secondary" onClick={() => openCreateNode(selected)}>Add child</Button>
                <Button size="sm" variant="secondary" onClick={() => openEditNode(selected)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleting(selected)}>Delete</Button>
              </div>
              <DataTable
                columns={productColumns}
                rows={products.items || []}
                rowKey={(r) => r.sku}
                loading={productsLoading}
                empty={
                  <EmptyState
                    title="No products"
                    body="No products are assigned to this category yet."
                    action={<Button size="sm" onClick={() => setAssignDrawer(true)}>Assign SKUs</Button>}
                  />
                }
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
                <span style={{ fontSize: 13, color: '#667' }}>
                  {products.total} products · showing {products.total ? offset + 1 : 0}–
                  {Math.min(offset + PAGE_SIZE, products.total)}
                </span>
                <span style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                    Prev
                  </Button>
                  <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= products.total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                    Next
                  </Button>
                </span>
              </div>
            </>
          ) : (
            <EmptyState
              title="Select a category"
              body="Pick a category on the left to see and manage its products."
            />
          )}
        </Card>
      </div>

      {nodeDrawer && (
        <Drawer
          title={nodeDrawer.mode === 'create'
            ? nodeDrawer.parent
              ? `New category under "${nodeDrawer.parent.label}"`
              : 'New top-level category'
            : `Edit category: ${nodeDrawer.node.label}`}
          onClose={() => setNodeDrawer(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setNodeDrawer(null)}>Cancel</Button>
              <Button onClick={saveNode} disabled={busy}>
                {nodeDrawer.mode === 'create' ? 'Create' : 'Save'}
              </Button>
            </>
          }
        >
          {nodeDrawer.mode === 'create' ? (
            <div className="pim-field">
              <label>Code</label>
              <input
                type="text"
                value={nodeForm.code}
                placeholder="unique code, e.g. pool_pumps"
                onChange={(e) => setNodeForm((f) => ({ ...f, code: e.target.value }))}
              />
              <p className="pim-muted" style={{ marginTop: 4, fontSize: 12 }}>
                Codes are unique across all trees (they key the BigCommerce category mapping).
              </p>
            </div>
          ) : (
            <div className="pim-field">
              <label>Code</label>
              <input type="text" value={nodeForm.code} disabled />
            </div>
          )}
          <div className="pim-field">
            <label>Label</label>
            <input
              type="text"
              value={nodeForm.label}
              onChange={(e) => setNodeForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>
          <div className="pim-field">
            <label>Parent</label>
            <select
              value={nodeForm.parent_id ?? ''}
              onChange={(e) =>
                setNodeForm((f) => ({ ...f, parent_id: e.target.value === '' ? null : Number(e.target.value) }))
              }
            >
              <option value="">(top level)</option>
              {parentOptions.map((n) => (
                <option key={n.id} value={n.id}>
                  {'\u00A0'.repeat(n.depth * 3)}{n.label}
                </option>
              ))}
            </select>
          </div>
          <div className="pim-field">
            <label>Sort order</label>
            <input
              type="number"
              value={nodeForm.sort_order}
              onChange={(e) => setNodeForm((f) => ({ ...f, sort_order: e.target.value }))}
            />
          </div>
        </Drawer>
      )}

      {assignDrawer && selected && (
        <Drawer
          title={`Assign SKUs to "${selected.label}"`}
          sub="Paste or type SKUs separated by spaces, commas, or new lines."
          onClose={() => setAssignDrawer(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setAssignDrawer(false)}>Cancel</Button>
              <Button onClick={submitAssign} disabled={busy}>Assign</Button>
            </>
          }
        >
          <div className="pim-field">
            <label>SKUs</label>
            <textarea
              rows={10}
              value={assignSkus}
              placeholder={'WC12345\nWC67890'}
              onChange={(e) => setAssignSkus(e.target.value)}
            />
          </div>
        </Drawer>
      )}

      {deleting && (
        <ConfirmModal
          title={`Delete category "${deleting.label}"?`}
          body="This removes the category, all its subcategories, and their product assignments. Affected products are re-synced (web tree changes propagate to Akeneo/BigCommerce)."
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteNode}
          onCancel={() => setDeleting(null)}
          busy={busy}
        />
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
